import {
	Card,
	Button,
	Tag,
	Space,
	Typography,
	Row,
	Col,
	Descriptions,
	Table,
	Divider,
	notification,
	Spin,
	Badge,
	Modal,
	Form,
	Select,
} from "antd";
import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
	PiArrowLeftDuotone,
	PiReceiptDuotone,
	PiCurrencyDollarDuotone,
	PiCalendarDuotone,
	PiUserDuotone,
	PiPackageDuotone,
	PiInfoDuotone,
	PiCheckCircleDuotone,
	PiShoppingCartDuotone,
	PiCreditCardDuotone,
	PiCloudDuotone,
	PiGlobeDuotone,
} from "react-icons/pi";
import dayjs from "dayjs";
import { formatCurrency } from "../../utils/Formatter";
import {
	requestGetTransactionList,
	requestPayInvoice,
	requestShowInvoice,
} from "../../services/user.service";

const { Title, Text } = Typography;

function InvoiceViewPage() {
	const navigate = useNavigate();
	const { teamId, id } = useParams();
	const [loading, setLoading] = useState(false);
	const [invoice, setInvoice] = useState(null);
	const [transactions, setTransactions] = useState([]);
	const [transactionsLoading, setTransactionsLoading] = useState(false);
	const [transactionModalVisible, setTransactionModalVisible] = useState(false);
	const [transactionLoading, setTransactionLoading] = useState(false);
	const [transactionCreated, setTransactionCreated] = useState(false);
	const [transactionForm] = Form.useForm();
	const method = Form.useWatch("method", transactionForm);

	useEffect(() => {
		document.title = "Cloudiva App | Invoice Details";
	}, []);

	useEffect(() => {
		if (teamId && id) {
			fetchInvoiceDetails();
			fetchTransactions();
		}
	}, [teamId, id]);

	const fetchInvoiceDetails = () => {
		setLoading(true);
		requestShowInvoice(teamId, id)
			.then((response) => {
				const invoiceData = response.data.data || response.data;
				setInvoice(invoiceData);
			})
			.catch((error) => {
				console.error("Error fetching invoice details:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch invoice details",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const fetchTransactions = () => {
		setTransactionsLoading(true);
		requestGetTransactionList(teamId, {
			filter: `invoice:${id}`,
		})
			.then((response) => {
				const transactionData = response.data.data || response.data;
				setTransactions(transactionData);
			})
			.catch((error) => {
				console.error("Error fetching transactions:", error);
				// Don't show error notification for transactions as it's not critical
			})
			.finally(() => {
				setTransactionsLoading(false);
			});
	};

	const handleCreateTransaction = () => {
		if (transactions.length > 0) {
			// find pending transactions
			const pendingTransactions = transactions.filter(
				(transaction) => transaction.status === "PENDING"
			);
			if (pendingTransactions.length > 0) {
				// If there are pending transactions, use the first one
				transactionForm.setFieldsValue({
					method: pendingTransactions[0].method,
					channel: pendingTransactions[0].channel,
				});
				setTransactionCreated(true);
				setTransactionModalVisible(true);
				return;
			}
		}
		// If no pending transactions, reset form and open modal
		setTransactionCreated(false);
		transactionForm.resetFields();
		setTransactionModalVisible(true);
	};

	const handleTransactionSubmit = (values) => {
		setTransactionLoading(true);
		const payload = {
			invoiceId: id,
			method: values.method,
			channel: values.channel,
		};

		requestPayInvoice(teamId, id, payload)
			.then((response) => {
				notification.success({
					message: "Success",
					description: "Transaction created successfully",
				});
				setTransactionModalVisible(false);
				transactionForm.resetFields();
				// Refresh invoice details and transactions to show new transaction
				fetchInvoiceDetails();
				fetchTransactions();

				const { invoice, transaction } = response.data.data;
				const paymentUrl = transaction?.metadata?.paymentUrl;

				if (paymentUrl) {
					window.open(paymentUrl, "_blank");
				}
			})
			.catch((error) => {
				console.error("Error creating transaction:", error);
				notification.error({
					message: "Error",
					description: error.response?.data?.message || "Failed to create transaction",
				});
			})
			.finally(() => {
				setTransactionLoading(false);
			});
	};

	const handleTransactionCancel = () => {
		setTransactionModalVisible(false);
		transactionForm.resetFields();
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "DRAFT":
				return "default";
			case "PENDING":
				return "orange";
			case "PAID":
				return "green";
			case "OVERDUE":
				return "red";
			case "CANCELLED":
				return "red";
			default:
				return "default";
		}
	};

	const getStatusText = (status) => {
		switch (status) {
			case "DRAFT":
				return "Draft";
			case "PENDING":
				return "Pending";
			case "PAID":
				return "Paid";
			case "OVERDUE":
				return "Overdue";
			case "CANCELLED":
				return "Cancelled";
			default:
				return status;
		}
	};

	const getTransactionStatusColor = (status) => {
		switch (status) {
			case "PENDING":
				return "orange";
			case "COMPLETED":
			case "SUCCESS":
				return "green";
			case "FAILED":
			case "CANCELLED":
				return "red";
			default:
				return "default";
		}
	};

	const getTransactionStatusText = (status) => {
		switch (status) {
			case "PENDING":
				return "Pending";
			case "COMPLETED":
			case "SUCCESS":
				return "Completed";
			case "FAILED":
				return "Failed";
			case "CANCELLED":
				return "Cancelled";
			default:
				return status;
		}
	};

	const itemColumns = [
		{
			title: "Tipe",
			key: "type",
			width: 100,
			render: (_, item) => (
				<Tag color={item.type === "DOMAIN" ? "green" : "blue"}>
					{item.type === "DOMAIN" ? "Domain" : "Instance"}
				</Tag>
			),
		},
		{
			title: "Detail Item",
			key: "itemDetails",
			render: (_, item) => {
				if (item.type === "DOMAIN") {
					// Domain item
					return (
						<div style={{ display: "flex", alignItems: "center" }}>
							<PiPackageDuotone size={16} style={{ marginRight: 8, color: "#52c41a" }} />
							<div>
								<div style={{ fontWeight: 500 }}>Domain {item.domain?.name || "N/A"}</div>
								<div style={{ fontSize: "12px", color: "#666" }}>
									{item.domain?.extension?.name} ({item.domain?.extension?.code})
									{/* {item.domain?._id && ` • ID: ${item.domain._id}`} */}
								</div>
							</div>
						</div>
					);
				}
				// Instance item
				return (
					<div style={{ display: "flex", alignItems: "center" }}>
						<PiPackageDuotone size={16} style={{ marginRight: 8, color: "#1890ff" }} />
						<div>
							<div style={{ fontWeight: 500 }}>{item.instance?.service?.name || "N/A"}</div>
							<div style={{ fontSize: "12px", color: "#666" }}>
								{item.instance?.plan?.name} ({item.instance?.plan?.code})
								{item.instance?.region?.name && ` • ${item.instance.region.name}`}
								{/* {item.instance?._id && ` • Instance: ${item.instance._id}`} */}
							</div>
						</div>
					</div>
				);
			},
		},
		{
			title: "Subtotal",
			dataIndex: "unitSubtotal",
			key: "unitSubtotal",
			width: 120,
			align: "center",
			render: (value) => {
				return (
					<div style={{ textAlign: "center" }}>{formatCurrency(value, invoice?.currency)}</div>
				);
			},
		},
		{
			title: "Kuantitas",
			dataIndex: "quantity",
			key: "quantity",
			width: 120,
			align: "center",
			render: (quantity, item) => (
				<Space size={2}>
					<div style={{ fontWeight: 500 }}>{quantity}</div>
					{item.type === "DOMAIN" ? (
						<div style={{ fontSize: "12px", color: "#666" }}>year{quantity > 1 ? "s" : ""}</div>
					) : (
						item.instance?.plan?.price?.interval && (
							<div style={{ fontSize: "12px", color: "#666" }}>
								{item.instance.plan.price.interval?.toLowerCase()}
							</div>
						)
					)}
				</Space>
			),
		},
		{
			title: "Total",
			dataIndex: "lineTotal",
			key: "lineTotal",
			width: 120,
			align: "center",
			render: (value) => {
				return (
					<div style={{ textAlign: "center" }}>{formatCurrency(value, invoice?.currency)}</div>
				);
			},
		},
		{
			title: "Status",
			key: "status",
			width: 120,
			align: "center",
			render: (_, item) => {
				if (item.type === "DOMAIN") {
					// Domain items - show domain status or generic active status
					if (item.domain._id) {
						return (
							<Link to={`/${teamId}/domains/${item.domain._id}`}>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										marginBottom: 4,
									}}>
									<PiGlobeDuotone size={16} style={{ marginRight: 4, color: "#52c41a" }} />
									<Tag color="green" size="small">
										Terdaftar
									</Tag>
								</div>
							</Link>
						);
					} else {
						return (
							<div style={{ textAlign: "center" }}>
								<Tag color="red" size="small">
									Blm Aktif
								</Tag>
							</div>
						);
					}
				}
				// Instance items - show provisioning status
				return (
					<div style={{ textAlign: "center" }}>
						{item.instance?._id ? (
							<Link to={`/${teamId}/instances/${item.instance._id}`}>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										marginBottom: 4,
									}}>
									<PiCloudDuotone size={16} style={{ marginRight: 4, color: "#52c41a" }} />
									<Tag color="green" size="small">
										Terprovisi
									</Tag>
								</div>
							</Link>
						) : (
							<div style={{ color: "#999" }}>
								<PiCloudDuotone size={16} style={{ marginBottom: 4, opacity: 0.5 }} />
								<div style={{ fontSize: "12px" }}>Blm Terprovisi</div>
							</div>
						)}
					</div>
				);
			},
		},
	];

	if (loading) {
		return (
			<div className="page-content">
				<div style={{ textAlign: "center", padding: "50px 0" }}>
					<Spin size="large" />
				</div>
			</div>
		);
	}

	if (!invoice) {
		return (
			<div className="page-content">
				<div style={{ textAlign: "center", padding: "50px 0" }}>
					<Text>Invoice not found</Text>
				</div>
			</div>
		);
	}

	return (
		<div className="page-content">
			<div className="page-content-wrapper">
				<Row gutter={24}>
					{/* Invoice Information */}
					<Col span={16}>
						<Card
							title={
								<Space>
									<PiReceiptDuotone size={20} />
									<span>Informasi Faktur</span>
								</Space>
							}
							extra={
								<Space>
									{invoice.status === "UNPAID" && (
										<>
											<Button
												type="primary"
												icon={<PiCreditCardDuotone />}
												onClick={handleCreateTransaction}>
												Bayar
											</Button>
										</>
									)}
								</Space>
							}
							style={{ marginBottom: 24 }}>
							<Descriptions column={2} bordered size="small">
								<Descriptions.Item label="Nomor Invoice" span={1}>
									<Text strong>{invoice.number}</Text>
								</Descriptions.Item>
								<Descriptions.Item label="Status" span={1}>
									<Tag color={getStatusColor(invoice.status)}>{getStatusText(invoice.status)}</Tag>
								</Descriptions.Item>
								<Descriptions.Item label="Mata Uang" span={1}>
									<Tag color="blue">{invoice.currency}</Tag>
								</Descriptions.Item>
								<Descriptions.Item label="Jumlah Item" span={1}>
									<Badge count={invoice.items?.length || 0} showZero />
								</Descriptions.Item>
								<Descriptions.Item label="Tanggal Diterbitkan" span={1}>
									<Space>
										<PiCalendarDuotone />
										{dayjs(invoice.issuedAt).format("DD MMMM YYYY, HH:mm")}
									</Space>
								</Descriptions.Item>
								{invoice.expiredAt && (
									<Descriptions.Item label="Tanggal Jatuh Tempo" span={1}>
										<Space>
											<PiCalendarDuotone />
											{dayjs(invoice.expiredAt).format("DD MMMM YYYY, HH:mm")}
										</Space>
									</Descriptions.Item>
								)}
								{invoice.order && (
									<Descriptions.Item label="Nomor Pesanan" span={2}>
										<Space>
											<PiShoppingCartDuotone />
											<Button
												type="link"
												style={{ padding: 0 }}
												onClick={() => navigate(`/${teamId}/orders/${invoice.order._id}`)}>
												{invoice.order.number}
											</Button>
										</Space>
									</Descriptions.Item>
								)}
							</Descriptions>
						</Card>

						{/* Invoice Items */}
						<Card
							title={
								<Space>
									<PiPackageDuotone size={20} />
									<span>Item Faktur</span>
									<Badge count={invoice.items?.length || 0} />
								</Space>
							}
							style={{ marginBottom: 24 }}>
							<Table
								columns={itemColumns}
								dataSource={invoice.items}
								pagination={false}
								rowKey={(item, index) => `${invoice._id}-${index}`}
								size="small"
								summary={(pageData) => {
									const subtotal = invoice.subtotal || 0;
									const tax = invoice.taxTotal || 0;
									const discount = invoice.discountTotal || 0;
									const total = invoice.total || 0;

									// show tax list, group by invoice.items.taxes.code
									const taxGroups = invoice.items.reduce((acc, item) => {
										item.taxes.forEach((tax) => {
											if (!acc[tax.code]) {
												acc[tax.code] = {
													name: tax.name,
													amount: 0,
												};
											}
											acc[tax.code].amount += tax.amount;
										});
										return acc;
									}, {});

									return (
										<Table.Summary>
											<Table.Summary.Row>
												<Table.Summary.Cell index={0} colSpan={5}>
													<Text strong>Subtotal</Text>
												</Table.Summary.Cell>
												<Table.Summary.Cell index={1} align="right">
													<Text strong>{formatCurrency(subtotal, invoice.currency)}</Text>
												</Table.Summary.Cell>
											</Table.Summary.Row>
											{discount > 0 && (
												<Table.Summary.Row>
													<Table.Summary.Cell index={0} colSpan={5}>
														<Text type="success">Diskon</Text>
													</Table.Summary.Cell>
													<Table.Summary.Cell index={1} align="right">
														<Text type="success">
															-{formatCurrency(discount, invoice.currency)}
														</Text>
													</Table.Summary.Cell>
												</Table.Summary.Row>
											)}
											{Object.entries(taxGroups).length > 0 &&
												Object.entries(taxGroups).map(([code, { name, amount }]) => (
													<Table.Summary.Row key={code}>
														<Table.Summary.Cell index={0} colSpan={5}>
															<Text>{name}</Text>
														</Table.Summary.Cell>
														<Table.Summary.Cell index={1} align="right">
															<Text>{formatCurrency(amount, invoice.currency)}</Text>
														</Table.Summary.Cell>
													</Table.Summary.Row>
												))}
											{/* {tax > 0 && (
												<Table.Summary.Row>
													<Table.Summary.Cell index={0} colSpan={5}>
														<Text>Tax</Text>
													</Table.Summary.Cell>
													<Table.Summary.Cell index={1} align="right">
														<Text>{formatCurrency(tax, invoice.currency)}</Text>
													</Table.Summary.Cell>
												</Table.Summary.Row>
											)} */}
											<Table.Summary.Row>
												<Table.Summary.Cell index={0} colSpan={5}>
													<Text strong style={{ fontSize: "16px" }}>
														Total Keseluruhan
													</Text>
												</Table.Summary.Cell>
												<Table.Summary.Cell index={1} align="right">
													<Text strong style={{ fontSize: "16px", color: "#1890ff" }}>
														{formatCurrency(total, invoice.currency)}
													</Text>
												</Table.Summary.Cell>
											</Table.Summary.Row>
										</Table.Summary>
									);
								}}
							/>
						</Card>

						{/* Payment History */}
						<Card
							title={
								<Space>
									<PiCreditCardDuotone size={20} />
									<span>Riwayat Pembayaran</span>
									<Badge count={transactions.length} />
								</Space>
							}
							style={{ marginBottom: 24 }}
							loading={transactionsLoading}>
							{transactions.length > 0 ? (
								<div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
									{transactions.map((transaction) => (
										<div
											key={transaction._id}
											style={{
												border: "1px solid #f0f0f0",
												borderRadius: "8px",
												padding: "16px",
												backgroundColor: "#fafafa",
											}}>
											<div
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "flex-start",
													marginBottom: "8px",
												}}>
												<div>
													<div
														style={{
															fontWeight: 500,
															marginBottom: "4px",
															display: "flex",
															alignItems: "center",
															gap: "8px",
														}}>
														{transaction.number}
														<Tag color={getTransactionStatusColor(transaction.status)}>
															{getTransactionStatusText(transaction.status)}
														</Tag>
													</div>
													<div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
														{transaction.team?.name || "Unknown Team"}
													</div>
													<div style={{ fontSize: "12px", color: "#666" }}>
														{dayjs(transaction.createdAt).format("DD MMM YYYY, HH:mm")}
													</div>
												</div>
												<div style={{ textAlign: "right" }}>
													<div style={{ fontWeight: 600, fontSize: "16px", marginBottom: "4px" }}>
														{formatCurrency(transaction.amount, transaction.currency)}
													</div>
													{transaction.metadata?.invoiceNumber && (
														<div style={{ fontSize: "12px", color: "#666" }}>
															Ref: {transaction.metadata.invoiceNumber}
														</div>
													)}
												</div>
											</div>
											<div
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
												}}>
												<div
													style={{ display: "flex", gap: "12px", fontSize: "12px", color: "#666" }}>
													<span>
														<strong>Metode:</strong> {transaction.method.replace("_", " ")} (
														{transaction.channel.replace("_", " ")})
													</span>
													<span>
														<strong>Kategori:</strong> {transaction.category}
													</span>
												</div>
												<Button
													type="dashed"
													onClick={() => navigate(`/${teamId}/transactions/${transaction._id}`)}>
													Lihat Detail
												</Button>
											</div>
										</div>
									))}
								</div>
							) : (
								<div style={{ textAlign: "center", padding: "20px 0", color: "#666" }}>
									<PiCreditCardDuotone size={48} style={{ marginBottom: "8px", opacity: 0.5 }} />
									<div>Belum ada riwayat pembayaran</div>
								</div>
							)}
						</Card>
					</Col>

					{/* Billing & Additional Info */}
					<Col span={8}>
						{/* Billing Address */}
						<Card
							title={
								<Space>
									<PiUserDuotone size={20} />
									<span>Alamat Penagihan</span>
								</Space>
							}
							style={{ marginBottom: 24 }}>
							{invoice.billingAddress ? (
								<div>
									<div style={{ marginBottom: 8 }}>
										<Text strong>{invoice.billingAddress.name}</Text>
									</div>
									<div style={{ marginBottom: 4 }}>
										<Text>{invoice.billingAddress.email}</Text>
									</div>
									<div style={{ marginBottom: 4 }}>
										<Text>{invoice.billingAddress.phone}</Text>
									</div>
									{invoice.billingAddress.address && (
										<>
											<Divider style={{ margin: "8px 0" }} />
											<div style={{ fontSize: "12px", color: "#666" }}>
												<div>{invoice.billingAddress.address}</div>
												<div>
													{invoice.billingAddress.city}, {invoice.billingAddress.state}{" "}
													{invoice.billingAddress.postalCode}
												</div>
												<div>{invoice.billingAddress.country}</div>
											</div>
										</>
									)}
								</div>
							) : (
								<Text type="secondary">Tidak ada alamat penagihan yang tersedia</Text>
							)}
						</Card>

						{/* Financial Summary */}
						<Card
							title={
								<Space>
									<PiCurrencyDollarDuotone size={20} />
									<span>Ringkasan Pembayaran</span>
								</Space>
							}
							style={{ marginBottom: 24 }}>
							<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text>Subtotal:</Text>
									<Text>{formatCurrency(invoice.subtotal || 0, invoice.currency)}</Text>
								</div>
								{invoice.discountTotal > 0 && (
									<div style={{ display: "flex", justifyContent: "space-between" }}>
										<Text type="success">Diskon:</Text>
										<Text type="success">
											-{formatCurrency(invoice.discountTotal, invoice.currency)}
										</Text>
									</div>
								)}
								{invoice.taxTotal > 0 && (
									<div style={{ display: "flex", justifyContent: "space-between" }}>
										<Text>Pajak:</Text>
										<Text>{formatCurrency(invoice.taxTotal, invoice.currency)}</Text>
									</div>
								)}
								<Divider style={{ margin: "8px 0" }} />
								<div style={{ display: "flex", justifyContent: "space-between" }}>
									<Text strong style={{ fontSize: "16px" }}>
										Total Keseluruhan:
									</Text>
									<Text strong style={{ fontSize: "16px", color: "#1890ff" }}>
										{formatCurrency(invoice.total || 0, invoice.currency)}
									</Text>
								</div>
							</div>
						</Card>

						{/* Payment Status */}
						<Card
							title={
								<Space>
									<PiCheckCircleDuotone size={20} />
									<span>Status Pembayaran</span>
								</Space>
							}
							style={{ marginBottom: 24 }}>
							<div style={{ textAlign: "center", padding: "20px 0" }}>
								<Tag
									color={getStatusColor(invoice.status)}
									style={{ fontSize: "14px", padding: "8px 16px" }}>
									{getStatusText(invoice.status)}
								</Tag>
								{invoice.status === "PAID" && invoice.paidAt && (
									<div style={{ marginTop: 12, fontSize: "12px", color: "#666" }}>
										Dibayar pada {dayjs(invoice.paidAt).format("DD MMMM YYYY, HH:mm")}
									</div>
								)}
								{invoice.status === "PENDING" && invoice.expiredAt && (
									<div style={{ marginTop: 12, fontSize: "12px", color: "#666" }}>
										Jatuh tempo {dayjs(invoice.expiredAt).format("DD MMMM YYYY")}
									</div>
								)}
							</div>
						</Card>

						{/* Metadata */}
						{invoice.metadata && Object.keys(invoice.metadata).length > 0 && (
							<Card
								title={
									<Space>
										<PiInfoDuotone size={20} />
										<span>Informasi Tambahan</span>
									</Space>
								}>
								<div style={{ backgroundColor: "#f5f5f5", padding: "12px", borderRadius: "4px" }}>
									<pre style={{ margin: 0, fontSize: "12px" }}>
										{JSON.stringify(invoice.metadata, null, 2)}
									</pre>
								</div>
							</Card>
						)}
					</Col>
				</Row>
			</div>

			{/* Transaction Modal */}
			<Modal
				title={
					<Space>
						<PiCreditCardDuotone />
						<span>Buat Pembayaran</span>
					</Space>
				}
				open={transactionModalVisible}
				onCancel={handleTransactionCancel}
				footer={null}
				width={500}>
				<Form
					form={transactionForm}
					layout="vertical"
					onFinish={handleTransactionSubmit}
					style={{ marginTop: 16 }}>
					<Form.Item
						label="Metode"
						name="method"
						rules={[{ required: true, message: "Pilih metode pembayaran" }]}>
						<Select disabled={transactionCreated} placeholder="Pilih metode pembayaran">
							{invoice.paymentMethods.map((method) => (
								<Select.Option key={method} value={method}>
									{method === "VIRTUAL_ACCOUNT" ? "Akun Virtual (VA)" : "Tunai"}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
					{/* channel */}
					<Form.Item
						label="Saluran"
						name="channel"
						rules={[{ required: true, message: "Pilih saluran pembayaran" }]}>
						<Select disabled={transactionCreated} placeholder="Pilih saluran pembayaran">
							{/* virtual account */}
							{method === "VIRTUAL_ACCOUNT" &&
								invoice.paymentChannels.map((channel) => (
									<Select.Option key={channel} value={channel}>
										{channel.replace("_", " ").toUpperCase()}
									</Select.Option>
								))}
							{/* cash */}
							{method === "CASH" && <Select.Option value="CASH">Cash</Select.Option>}{" "}
						</Select>
					</Form.Item>

					<Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
						<Space style={{ width: "100%", justifyContent: "flex-end" }}>
							<Button onClick={handleTransactionCancel}>Batal</Button>
							<Button type="primary" htmlType="submit" loading={transactionLoading}>
								Bayar
							</Button>
						</Space>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
}

export default InvoiceViewPage;
